import {
  Alert,
  Button,
  Col,
  Collapse,
  CollapseProps,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Tag,
  Tooltip,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { ChargeType } from './ChargesType';
import { TaxFormInputs } from './modal-tax';
import { CaretRightOutlined } from '@ant-design/icons'
import { Countries, EXCLUDED_COUNTRIES, StatesByCountry } from '../../constants';

export type ChargesFormInputs = {
  type: ChargeType;
  value: number;
  taxes: TaxFormInputs[];
};

const TaxesForm = ({
  tax,
  onCreate,
  setTaxEdit,
}:  {
  tax?: TaxFormInputs | null;
  onCreate?: (values: TaxFormInputs) => void;
  setTaxEdit?: (value: TaxFormInputs) => void;
}) => {
  const [form] = Form.useForm();
  const [taxLocations, setTaxLocations] = useState<string[]>([]);
  const [countrySelected, setCountrySelected] = useState("ARG");

  const createResponse = (tax: TaxFormInputs) => {
    tax.exceptions = [{ locations: taxLocations }];

    setTaxLocations(null);
    onCreate(tax);
  }

  const validateForm = () => {
    form
      .validateFields()
      .then((values) => {
        form.resetFields();
        createResponse(values);
      })
      .catch((info) => {
        console.error('Validate Failed:', info);
      });
  }

  useEffect( () => {
    if(tax !== null) {
      form.setFieldValue("name", tax?.name);
      form.setFieldValue('value', tax?.value);
      form.setFieldValue('exceptions', tax?.exceptions[0]?.locations);
    }
  }, [tax]);

  return (
    <Form<TaxFormInputs>
      form={form}
      layout="vertical"
      name="form_into_modal"
    >
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item<TaxFormInputs>
            name="name"
            label="Nombre"
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Tooltip trigger={['hover']} title={"Porcentual sobre el monto del cargo"} placement="topLeft">
            <Form.Item<TaxFormInputs>
              name="value"
              label="Valor"
            >
              <Input />
            </Form.Item>
          </Tooltip>
        </Col>
        <Col span={12}>
          <Form.Item>
            <Select
              placeholder="Seleccionar pais"
              showSearch
              disabled
              defaultValue={countrySelected}
              onChange={ setCountrySelected }
              options={Object.entries(Countries)
                .filter(([code]) => !EXCLUDED_COUNTRIES.includes(code))
                .map(([code, name]) => ({ label: name, value: code }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Tooltip trigger={['hover']} title={"Provincias a exceptuar el impuesto"} placement="topLeft">
            <Form.Item<TaxFormInputs>
              name="exceptions"
            >
              <Select
                mode="multiple"
                placeholder="Provincias a exceptuar"
                showSearch
                onChange={(value: string[]) => setTaxLocations(value) }
                options={Object.entries(StatesByCountry?.['ARG'] ?? {}).map(([stateCode, name]) => ({
                  label: name,
                  value: stateCode,
                }))}
              />
            </Form.Item>
          </Tooltip>
        </Col>
        <Col span={24} style={{ textAlign: 'right' }}>
          <Button style={{ marginRight: '10px' }} type="default" onClick={ () => {
              form.resetFields();
              setTaxEdit(null);
            }}
          >
            Cancelar
          </Button>
          <Button type="primary" onClick={ validateForm }>
            Agregar
          </Button>
        </Col>
      </Row>
    </Form>
  )
};

const ModalCharges = ({
  open,
  onCreate,
  onCancel,
  charge
} : {
  open: boolean;
  onCreate: (values: ChargesFormInputs) => void;
  onCancel: () => void;
  charge?: ChargesFormInputs | undefined;
}) => {
  const [form] = Form.useForm();
  const [taxes, setTaxes] = useState<TaxFormInputs[]>([]);
  const [taxEdit, setTaxEdit] = useState<TaxFormInputs>(null);

  const createTax = async (values: TaxFormInputs) => {
    if(taxes?.length > 0 ) {
      setTaxes((prev: any) => {
        return [...prev, values];
      });
    } else {
      setTaxes([values]);
    }
  }

  const deleteTax = (taxDelete: TaxFormInputs) => {
    setTaxes( (prev: TaxFormInputs[]) => {
      return prev?.filter(tax => tax?.name !== taxDelete?.name && tax?.value !== taxDelete?.value );
    });
  }

  const validateTaxAction = async (value: TaxFormInputs) => {

    if(taxEdit !== null) {
      deleteTax(taxEdit);
      await createTax(value);
      setTaxEdit(null);
    } else {
      await createTax(value);
    }

  }

  const setTaxEditValue = (taxEdit: TaxFormInputs) => {
    setTaxEdit(taxes !== null ? taxes?.find( (tax) => tax?.name === taxEdit?.name) : null);
  }

  const getItems: () => CollapseProps['items'] = () => [
    {
      key: '1',
      label: taxEdit !== null ? 'Modificar impuesto' : 'Agregar impuesto',
      children: <TaxesForm tax={ taxEdit } onCreate={ validateTaxAction } setTaxEdit={ setTaxEdit } />,
    }
  ];

  useEffect(() => {
    if(charge !== undefined ) {
      form.setFieldValue("type", charge?.type);
      form.setFieldValue("value", charge?.value);
      setTaxes(charge?.taxes);
    }
  }, [charge]);

  return (
    <>
      <Modal
        open={open}
        title={charge ? 'Modificar cargo' : 'Nuevo cargo'}
        okText={charge ? 'Modificar' : 'Crear'}
        cancelText="Cancelar"
        onCancel={() => {
          form.resetFields();
          onCancel();
        }}
        onOk={() => {
          form
            .validateFields()
            .then((values: ChargesFormInputs) => {
              values.taxes = taxes;
              onCreate(values);
              form.resetFields();
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Form form={form} layout="vertical" name="form_in_modal">
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item<ChargesFormInputs>
                name="type"
                label="Tipo"
                rules={[{ required: true, message: 'Por favor seleccionar una opcion.' }]}
              >
                <Select
                  placeholder="Seleccionar tipo"
                  showSearch
                  options={Object.values(ChargeType).map((type) => ({ label: type, value: type }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<ChargesFormInputs>
                name="value"
                label="Valor"
                rules={[{ required: true, message: 'Por favor ingresar valor.' }]}
              >
                <InputNumber<number>
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                  }
                  parser={(value) =>
                    value?.replace(/\$\s?|(\.*)/g, '').replace(/\./g, '') as unknown as number
                  }
                  style={{ width: '100%' }}
                  controls={false}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Collapse
                bordered={false}
                defaultActiveKey={['1']}
                expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                items={getItems()}
              />
            </Col>
          </Row>
          {
            taxes?.length > 0 ? (
              <Row>
                <Col span={24} style={{ margin: '5px' }}>
                  <h2> Impuestos</h2>
                  {
                    taxes?.map((tax) => {
                      return (
                        <Tag
                          key={tax?.value}
                          bordered={false}
                          style={{ cursor: 'pointer' }}
                          color="purple"
                          closable={true}
                          onClick={() => {
                            setTaxEditValue(tax);
                          }}
                          onClose={() => {
                            deleteTax(tax);
                          }}
                        >
                          {tax?.name}
                        </Tag>
                      );
                    })
                  }
                </Col>
              </Row>
            ) : (
              <Row>
                <Col span={24} style={{ margin: '5px' }}>
                  <h2> Impuestos</h2>
                </Col>
                <Col span={24}>
                  <Alert
                    message="No tienes impuestos para mostrar"
                    type="info"
                    showIcon
                    style={{ marginBottom: '2rem' }}
                  />
                </Col>
              </Row>
            )
          }
          <Row gutter={24} style={{ marginTop: '20px' }}>
            <Col span={24}>
              <Alert
                message="Ejemplo"
                description="Sobre un cargo de tipo CREATE de $500 se agrega un impuesto con un valor porcentual del 1% sobre el monto principal, el valor final se refleja en $505. En caso de ser necesario, el impuesto puede exceptuarse en varias provincias."
                type="info"
                showIcon
                closable
              />
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  )
};

export default ModalCharges;